import { useState, useEffect, useMemo } from "react";
import useImage from "use-image";
import { designBackgroundApi } from "../../../api/designBackground";
import defaultAvatarUrl from "../../../assets/images/default-avatar.png";
import { DEFAULT_BOX } from "../constant";

// ===== utilities: px <-> % =====
const toPct = (px, total) => (px / total) * 100;
const fromPct = (pct, total) => (pct / 100) * total;

export default function useEditorLogic(backgroundId, toast) {
  // ================== STATES ==================
  const [bgList, setBgList] = useState([]);
  const [imgUrl, setImgUrl] = useState("");
  const [layout, setLayout] = useState(null);
  const [sel, setSel] = useState(null);

  const [preview, setPreview] = useState({
    name: "HỌ TÊN",
    code: "MSNV",
    jobTitle: "Chuyên viên kinh doanh",
    info: "ABC",
    amount: "12.345",
    transactionTemplate: "{{so_luong_giao_dich}} Giao dịch thành công căn hộ",
    transactionCount: "01",
  });

  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(defaultAvatarUrl);

  // canvas size
  const [img] = useImage(imgUrl, "anonymous");
  const [naturalW, setNaturalW] = useState(1080);
  const [naturalH, setNaturalH] = useState(1620);
  const [viewW, setViewW] = useState(1080);
  const [viewH, setViewH] = useState(1620);

  // ================== LOAD BACKGROUND LIST ==================
  useEffect(() => {
    designBackgroundApi.list().then((res) => {
      setBgList(res?.items || []);
    });
  }, []);

  // ================== LOAD ONE BACKGROUND ==================
  useEffect(() => {
    if (!backgroundId) return;

    async function load() {
      try {
        const [bgRes, layoutRes] = await Promise.all([
          designBackgroundApi.getOne(backgroundId),
          designBackgroundApi.getLayout(backgroundId),
        ]);

        const bg = bgRes || {};
        const lay = layoutRes || {};

        setImgUrl(toPublicUrl(bg.url || ""));
        setLayout(
          Object.keys(lay || {}).length ? lay : { ...DEFAULT_BOX }
        );

        setPreview((p) => ({
          ...p,
          info: bg.infoText || "ABC",
          transactionTemplate:
            bg.transactionText ||
            "{{so_luong_giao_dich}} Giao dịch thành công căn hộ",
        }));

        toast && toast({ status: "success", title: "Đã tải background" });
      } catch (e) {}
    }
    load();
  }, [backgroundId]);

  // get correct public URL
  const toPublicUrl = (p) => {
    if (!p) return "";
    if (p.startsWith("http")) return p;
    const base = process.env.REACT_APP_BACKEND_API || "";
    return `${base}/${p}`.replace(/([^:]\/)\/+/g, "$1");
  };

  // ================== IMAGE NATURAL SIZE ==================
  useEffect(() => {
    if (!img) return;
    setNaturalW(img.width || 1080);
    setNaturalH(img.height || 1620);
    setViewW(layout.refW ||img.width || 1080);
    setViewH(layout.refH ||img.height || 1620);
  }, [img]);

  // ================== COMPUTED PX LAYOUT ==================
  const pxLayout = useMemo(() => {
    if (!layout || !viewW || !viewH) return null;

    const out = {};
    Object.keys(layout).forEach((k) => {
      if (k === "avatar") {
        out.avatar = {
          cx: fromPct(layout.avatar.cx, viewW),
          cy: fromPct(layout.avatar.cy, viewH),
          d: fromPct(layout.avatar.d, viewW),
        };
      } else if (layout[k]?.x !== undefined) {
        out[k] = {
          x: fromPct(layout[k].x, viewW),
          y: fromPct(layout[k].y, viewH),
          w: fromPct(layout[k].w, viewW),
          h: fromPct(layout[k].h, viewH),
        };
      }
    });

    return out;
  }, [layout, viewW, viewH]);

  // ================== UPDATE LAYOUT (% FROM PX) ==================
  const updatePct = (key, newShapePx) => {
    if (!newShapePx) return;

    if (key === "avatar") {
      setLayout((l) => ({
        ...l,
        avatar: {
          cx: toPct(newShapePx.cx, viewW),
          cy: toPct(newShapePx.cy, viewH),
          d: toPct(newShapePx.d, viewW),
        },
      }));
      return;
    }

    setLayout((l) => ({
      ...l,
      [key]: {
        ...l[key],
        x: toPct(newShapePx.x, viewW),
        y: toPct(newShapePx.y, viewH),
        w: toPct(newShapePx.w, viewW),
        h: toPct(newShapePx.h, viewH),
      },
    }));
  };

  // ================== FONT CONTROLS ==================
  const currentFontSizePx = useMemo(() => {
    if (!sel || sel === "avatar" || !pxLayout) return 0;
    return Math.round((pxLayout[sel]?.h || 0) * 0.72);
  }, [sel, pxLayout]);

  const updateSelectedFontSizePx = (fontPx) => {
    if (!sel || sel === "avatar") return;
    const h = Math.max(4, Math.round(fontPx / 0.72));
    updatePct(sel, { ...pxLayout[sel], h });
  };

  const currentWeight = useMemo(() => {
    if (!sel || sel === "avatar") return 700;
    return Number(layout?.[sel]?.weight || 700);
  }, [sel, layout]);

  const updateSelectedWeight = (w) => {
    if (sel && sel !== "avatar") {
      setLayout((l) => ({
        ...l,
        [sel]: {
          ...l[sel],
          weight: Number(w),
        },
      }));
    }
  };

  const currentColor = useMemo(() => {
    if (!sel || sel === "avatar") return "#ffffff";
    return layout?.[sel]?.color || "#ffffff";
  }, [sel, layout]);

  const updateSelectedColor = (hex) => {
    if (sel && sel !== "avatar") {
      setLayout((l) => ({
        ...l,
        [sel]: {
          ...l[sel],
          color: hex,
        },
      }));
    }
  };

  // ================== TOGGLE FIELDS ==================
  const toggleField = (key, enabled) => {
    setLayout((l) => {
      if (enabled) {
        if (!l[key]) return { ...l, [key]: { ...DEFAULT_BOX[key] } };
        return l;
      }
      const { [key]: _, ...rest } = l;
      return rest;
    });
    if (sel === key) setSel(null);
  };

  // ================== SAVE LAYOUT ==================
  const handleSave = async () => {
    if (!backgroundId || !layout) return;

    const body = {
      refW: 1080,
      refH: 1620,
      ...layout,
      infoText: preview.info,
      transactionText:
        preview.transactionTemplate ||
        "{{so_luong_giao_dich}} Giao dịch thành công căn hộ",
    };

    try {
      await designBackgroundApi.updateLayout(backgroundId, body);
      toast && toast({ status: "success", title: "Đã lưu Banner" });
    } catch {}
  };

  // ================== RETURN HOOK API ==================
  return {
    // canvas & image
    imgUrl,
    img,
    viewW,
    viewH,
    setViewW,
    setViewH,
    naturalW,
    naturalH,

    // layout logic
    layout,
    pxLayout,
    sel,
    setSel,
    updatePct,
    toggleField,

    // preview text
    preview,
    setPreview,

    // avatar
    avatarPreviewUrl,

    // font controls
    currentFontSizePx,
    updateSelectedFontSizePx,
    currentWeight,
    updateSelectedWeight,
    currentColor,
    updateSelectedColor,

    // save
    handleSave,
  };
}
